/**
 * SC-209: everything inside the floating window.
 *
 * Rendered through a portal from `PipHost`, so it sits inside the main React tree and inherits Redux, the
 * LiveKit `RoomContext` and both theme providers — only its pixels are somewhere else. Nothing here reaches
 * for `window` or `document`, because in this component those are the *opener's*, not the window's: the size
 * arrives as props from the renderer, which is the thing that owns the window.
 */

import React, { useCallback, useEffect, useState } from 'react'
import { useMaybeRoomContext } from '@livekit/components-react'
import { RoomEvent } from 'livekit-client'

import { requestLiveKitAudioResume } from '../../../utils/livekitAudioResumeRequest'
import type { PipModel } from './pipModel'
import { pipLayout } from './pipModel'
import type { PipAutoOpen } from './pipPreferences'
import { readPipAutoOpen, writePipAutoOpen } from './pipPreferences'
import { PipSharedContentCell } from './PipSharedContentCell'
import { PipTileView } from './PipTile'
import { PipToolbar } from './PipToolbar'
import { SpeakerGlyph } from './pipIcons'
import type { PipOpenReason } from './renderers/PipRenderer'
import {
  audioResumeStripStyle,
  autoOpenNoteStyle,
  gridStyle,
  headerCountStyle,
  headerNameStyle,
  headerStyle,
  shellStyle,
} from './pipStyles'

export interface PipRootProps {
  model: PipModel
  /** The window's current inner size; the user drags it and we reflow, we never drive it. */
  width: number
  height: number
  onBackToTab(): void
  onClose(): void
  /** Why this window is open. Anything but `'user'` means nobody clicked, and is worth saying once. */
  openReason: PipOpenReason
}

/** Once a session, not once ever: a reminder is only annoying if it comes back the same afternoon. */
const AUTO_OPEN_NOTE_KEY = 'sevvPipAutoOpenNoticeShown'
const AUTO_OPEN_NOTE_MS = 5000
const AUTO_OPEN_NOTE = 'Opened automatically - turn off in the toolbar'

/**
 * R14/R2: tell the user once why a window they did not ask for is on their screen.
 *
 * A window appearing by itself is startling the first time and obvious thereafter, so this says it once per
 * session and then never again. `sessionStorage` rather than `localStorage` deliberately: a month later, in
 * a different frame of mind, the first automatic open is startling again.
 */
function useFirstAutoOpenNote(openReason: PipOpenReason): boolean {
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (openReason === 'user') return

    try {
      if (sessionStorage.getItem(AUTO_OPEN_NOTE_KEY)) return
      sessionStorage.setItem(AUTO_OPEN_NOTE_KEY, '1')
    } catch {
      // Storage refused. Showing the note once per window beats never explaining it at all.
    }

    setShow(true)
    const timer = setTimeout(() => setShow(false), AUTO_OPEN_NOTE_MS)
    return () => clearTimeout(timer)
  }, [openReason])

  return show
}

/**
 * SC-257 × SC-209: whether the browser is currently refusing to play remote audio.
 *
 * `useMaybeRoomContext` rather than `useRoomContext` so this renders in a test, or in any tree that has no
 * room yet, instead of throwing. No room means nothing is blocked.
 */
function useAudioPlaybackBlocked(): boolean {
  const room = useMaybeRoomContext()
  const [blocked, setBlocked] = useState(false)

  useEffect(() => {
    if (!room) {
      setBlocked(false)
      return
    }

    const sync = () => setBlocked(!room.canPlaybackAudio)
    sync()
    room.on(RoomEvent.AudioPlaybackStatusChanged, sync)
    return () => {
      room.off(RoomEvent.AudioPlaybackStatusChanged, sync)
    }
  }, [room])

  return blocked
}

function PipRootView({ model, width, height, onBackToTab, onClose, openReason }: PipRootProps) {
  const layout = pipLayout({
    width,
    height,
    tileCount: model.tiles.length,
    content: model.content?.orientation ?? null,
  })
  const audioBlocked = useAudioPlaybackBlocked()
  const showAutoOpenNote = useFirstAutoOpenNote(openReason)

  // Read once and held here only so the button can draw itself. Everything that acts on the preference
  // reads `localStorage` at the moment it needs it, so nothing can act on a stale copy.
  const [autoOpen, setAutoOpen] = useState<PipAutoOpen>(readPipAutoOpen)
  const toggleAutoOpen = useCallback(() => {
    setAutoOpen((current) => {
      const next: PipAutoOpen = current === 'on' ? 'never' : 'on'
      writePipAutoOpen(next)
      return next
    })
  }, [])

  const resumeAudio = useCallback(() => {
    // Synchronously, in the handler: the connector's resume runs on the user activation this click
    // propagates to the opener, and a later tick would not carry it.
    requestLiveKitAudioResume('pip-banner', window, { force: true })
  }, [])

  return (
    <div style={shellStyle} data-testid="pip-root">
      {/* The browser's title bar shows the origin and nothing else — that is a specification requirement,
          not a gap we can fill (see `headerStyle`). So the window says where you are itself. */}
      <div style={headerStyle}>
        <span style={headerNameStyle}>{model.roomName}</span>
        <span style={headerCountStyle} data-testid="pip-proximity-count">
          {model.proximityCount}
        </span>
      </div>

      {showAutoOpenNote && (
        <div style={autoOpenNoteStyle} data-testid="pip-auto-open-note">
          {AUTO_OPEN_NOTE}
        </div>
      )}

      <div style={gridStyle(layout)} data-testid="pip-grid">
        {model.content && layout.contentArea && (
          <PipSharedContentCell content={model.content} area={layout.contentArea} />
        )}
        {/* `selectPipTiles` caps the tiles at what the layout holds, so `model.overflow` is
            everyone left without one. */}
        {model.tiles.map((tile, index) => {
          const isLast = index === model.tiles.length - 1
          return (
            <PipTileView
              key={tile.participantId}
              tile={tile}
              videoEnabled={model.controls.videoEnabled}
              overflow={isLast ? model.overflow : 0}
              span={isLast ? layout.lastTileSpan : 'none'}
            />
          )
        })}
      </div>

      {audioBlocked && (
        <button type="button" onClick={resumeAudio} style={audioResumeStripStyle} data-testid="pip-audio-resume">
          <SpeakerGlyph size={13} />
          Audio paused - click to resume
        </button>
      )}

      <PipToolbar
        controls={model.controls}
        onBackToTab={onBackToTab}
        onClose={onClose}
        autoOpen={autoOpen}
        onToggleAutoOpen={toggleAutoOpen}
      />
    </div>
  )
}

/**
 * Memoised because everything it is given is stable between real changes — `PipHost` memoises the model and
 * the callbacks — so the window re-renders when the room changes and not when the controller does.
 */
export const PipRoot = React.memo(PipRootView)

export default PipRoot
